import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Shield, 
  Users, 
  FileText, 
  Building2, 
  Calendar,
  Activity,
  LogOut,
  Loader2,
  TrendingUp,
  Clock,
  UserCheck,
  Download,
  Upload,
  AlertTriangle,
  CloudDownload
} from 'lucide-react';
import { adminService, handleApiError } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import RestoreBackupModal from './RestoreBackupModal';
import SyncFromProductionModal from './SyncFromProductionModal';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);

  // Load admin statistics
  const loadStatistics = async () => {
    try {
      setLoading(true);
      const stats = await adminService.getStatistics();
      setStatistics(stats);
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible de charger les statistiques: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatistics();
  }, []);

  const handleLogout = () => {
    logout();
    toast({
      title: "Déconnexion",
      description: "Vous avez été déconnecté avec succès.",
    });
    navigate('/');
  };

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    
    try {
      // Download MongoDB native backup (.tar.gz file)
      const response = await adminService.downloadBackup();
      
      // The response is already a Blob from the API service
      const blob = response;
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with timestamp for MongoDB native backup
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `mongodb_backup_tennis_club_${timestamp}.tar.gz`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "✅ Backup MongoDB téléchargé",
        description: `Backup natif MongoDB enregistré avec succès (format .tar.gz)`,
      });
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible de télécharger le backup: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setDownloadingBackup(false);
    }
  };

  const handleRestoreBackup = async (file) => {
    try {
      const result = await adminService.restoreBackup(file);
      
      if (result.success) {
        // Reload statistics after restore
        await loadStatistics();
        
        toast({
          title: "✅ Restauration réussie",
          description: `${result.details.clubs_restored} clubs, ${result.details.members_restored} membres, ${result.details.pvs_restored} PV restaurés`,
        });
      }
      
      return result;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  };

  const handleSyncFromProduction = async () => {
    try {
      const result = await adminService.syncFromProduction();
      
      if (result.success) {
        // Reload statistics after sync
        await loadStatistics();
        
        toast({
          title: "✅ Synchronisation réussie",
          description: `${result.details.propositions_synced} propositions synchronisées depuis la production`,
          duration: 5000,
        });
      }
      
      return result;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-CH', { 
      timeZone: 'Europe/Zurich',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCommitteeDate = (dateString) => {
    if (!dateString) return 'Aucune date';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-CH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getConnectionStatus = (lastLogin) => {
    if (!lastLogin) return { status: 'Jamais connecté', color: 'bg-gray-100 text-gray-600' };
    
    const loginDate = new Date(lastLogin);
    const now = new Date();
    const diffDays = Math.floor((now - loginDate) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return { status: 'Aujourd\'hui', color: 'bg-green-100 text-green-700' };
    if (diffDays === 1) return { status: 'Hier', color: 'bg-blue-100 text-blue-700' };
    if (diffDays <= 7) return { status: `Il y a ${diffDays} jours`, color: 'bg-yellow-100 text-yellow-700' };
    if (diffDays <= 30) return { status: `Il y a ${diffDays} jours`, color: 'bg-orange-100 text-orange-700' };
    return { status: `Il y a ${diffDays} jours`, color: 'bg-red-100 text-red-700' };
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-green-600" />
            <p className="text-gray-600">Chargement des statistiques...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
              Dashboard Administrateur
            </h1>
            <p className="text-gray-600">
              Gestion et statistiques PV Manager • Connecté en tant que <strong>{user?.username}</strong>
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button 
            variant="outline"
            onClick={handleLogout}
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
          
          <Button 
            onClick={handleDownloadBackup}
            disabled={downloadingBackup}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloadingBackup ? 'Téléchargement...' : 'Télécharger Backup'}
          </Button>

          <Button 
            onClick={() => setShowRestoreModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Upload className="w-4 w-4 mr-2" />
            Restaurer Backup
          </Button>

          <Button 
            onClick={() => setShowSyncModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            <CloudDownload className="w-4 h-4 mr-2" />
            Synchroniser depuis Production
          </Button>

        </div>
      </div>

      {/* Overall Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="border-blue-200 bg-blue-50 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center">
              <Building2 className="w-10 h-10 text-blue-600 mr-4" />
              <div>
                <p className="text-2xl font-bold text-blue-700">
                  {statistics?.totals?.total_clubs || 0}
                </p>
                <p className="text-blue-600">Clubs inscrits</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-green-200 bg-green-50 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center">
              <Users className="w-10 h-10 text-green-600 mr-4" />
              <div>
                <p className="text-2xl font-bold text-green-700">
                  {statistics?.totals?.total_active_members || 0}
                </p>
                <p className="text-green-600">Membres actifs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-purple-200 bg-purple-50 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center">
              <FileText className="w-10 h-10 text-purple-600 mr-4" />
              <div>
                <p className="text-2xl font-bold text-purple-700">
                  {statistics?.totals?.total_pvs || 0}
                </p>
                <p className="text-purple-600">PV créés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-orange-200 bg-orange-50 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center">
              <Activity className="w-10 h-10 text-orange-600 mr-4" />
              <div>
                <p className="text-2xl font-bold text-orange-700">
                  {statistics?.totals?.active_last_week || 0}
                </p>
                <p className="text-orange-600">Actifs cette semaine</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clubs Table */}
      <Card className="border-gray-200 bg-white shadow-md">
        <CardHeader>
          <CardTitle className="text-gray-800 flex items-center">
            <Building2 className="w-5 h-5 mr-2" />
            Clubs Inscrits ({statistics?.clubs?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!statistics?.clubs || statistics.clubs.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aucun club inscrit</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Nom du Club</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Username</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Date d'inscription</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Dernière connexion</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Membres actifs</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">PV créés</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {statistics.clubs.map((club) => {
                    const connectionStatus = getConnectionStatus(club.last_login);
                    return (
                      <tr key={club.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                              <Building2 className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{club.name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                            {club.username}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-gray-600">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                            {formatDate(club.created_at).split(' à ')[0]}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-gray-600">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-2 text-gray-400" />
                            {formatDate(club.last_login)}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <div className="flex items-center justify-center">
                            <UserCheck className="w-4 h-4 mr-1 text-green-600" />
                            <span className="font-semibold text-green-700">{club.active_members}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <div className="flex items-center justify-center">
                            <FileText className="w-4 h-4 mr-1 text-purple-600" />
                            <span className="font-semibold text-purple-700">{club.total_pvs}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Badge className={connectionStatus.color}>
                            {connectionStatus.status}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Refresh Button */}
      <div className="mt-6 flex justify-center">
        <Button 
          onClick={loadStatistics}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          Actualiser les statistiques
        </Button>
      </div>

      {/* Restore Backup Modal */}
      <RestoreBackupModal 
        open={showRestoreModal}
        onOpenChange={setShowRestoreModal}
        onRestore={handleRestoreBackup}
      />

      <SyncFromProductionModal
        open={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        onSync={handleSyncFromProduction}
      />
    </div>
  );
};

export default AdminDashboard;